import React from "react";
import { useTranslation } from "react-i18next";
import LegalLayout from "../components/LegalLayout";
import { EMPRESA } from "../legal/empresa";

const TITULOS = { pt: "Política de Privacidade", en: "Privacy Policy", es: "Política de Privacidad" };
const ATUALIZADO = { pt: "18 de agosto de 2026", en: "August 18, 2026", es: "18 de agosto de 2026" };

export default function PoliticaPrivacidade() {
  const { i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || "pt").slice(0, 2);

  const CONTEUDO = {
    pt: (
      <>
        <p>
          Esta Política de Privacidade explica como o <strong>{EMPRESA.nomeFantasia}</strong>
          {" "}("nós", "sistema" ou "plataforma"), operado por {EMPRESA.razaoSocial}
          {" "}(CNPJ {EMPRESA.cnpj}), coleta, usa, armazena e protege os dados pessoais de
          quem visita nosso site ou utiliza o painel de gestão, em conformidade com a Lei
          Geral de Proteção de Dados (Lei nº 13.709/2018 — LGPD).
        </p>

        <h2>1. Quem é o controlador dos dados</h2>
        <p>
          O controlador dos dados tratados nesta plataforma é {EMPRESA.razaoSocial}, CNPJ
          {" "}{EMPRESA.cnpj}, com sede em {EMPRESA.cidade}/{EMPRESA.uf}. Para qualquer
          assunto relacionado a privacidade e proteção de dados, o titular pode entrar em
          contato pelo e-mail <a href={`mailto:${EMPRESA.contatoEmail}`}>{EMPRESA.contatoEmail}</a>.
        </p>

        <h2>2. Quais dados coletamos</h2>
        <p><strong>a) Dados fornecidos por você</strong> ao preencher o formulário de contato
          do site, criar uma conta ou usar o painel: nome, e-mail, WhatsApp/telefone, nome
          do negócio e, quando aplicável, CPF/CNPJ.</p>
        <p><strong>b) Dados operacionais</strong> que você (ou sua equipe) insere no uso normal
          do sistema para gerenciar seu próprio negócio: cadastro de clientes, veículos,
          ordens de serviço, orçamentos, itens de estoque e lançamentos financeiros. Esses
          dados pertencem a você e ao seu negócio — usamos apenas para operar a plataforma
          em seu nome.</p>
        <p><strong>c) Dados de navegação e uso</strong>, coletados automaticamente: endereço
          IP, tipo de dispositivo/navegador, páginas visitadas e cookies (ver nossa{" "}
          <a href="/cookies">Política de Cookies</a>).</p>
        <p><strong>d) Dados de pagamento</strong>: quando você assina um plano pago, o
          processamento do cartão/Pix é feito diretamente pelo Mercado Pago; nós armazenamos
          apenas o status e o histórico da cobrança, nunca o número completo do cartão.</p>

        <h2>3. Para que usamos seus dados</h2>
        <ul>
          <li>Viabilizar seu acesso e o funcionamento do painel de gestão contratado;</li>
          <li>Processar cobranças e manter o histórico de pagamentos e assinaturas;</li>
          <li>Prestar suporte técnico e comercial;</li>
          <li>Enviar comunicações operacionais (confirmação de cadastro, recuperação de
            senha, avisos de cobrança/licença);</li>
          <li>Cumprir obrigações legais e fiscais;</li>
          <li>Medir a performance de nossas campanhas de divulgação (ver seção de Cookies);</li>
          <li>Melhorar a plataforma com base em uso agregado e anônimo.</li>
        </ul>
        <p>Não vendemos dados pessoais a terceiros.</p>

        <h2>4. Base legal (LGPD, art. 7º)</h2>
        <p>
          Tratamos seus dados com base na <strong>execução de contrato</strong> (para prestar
          o serviço que você contratou), no <strong>cumprimento de obrigação legal ou
          regulatória</strong> (ex.: registros fiscais), no <strong>legítimo interesse</strong>
          {" "}(segurança da plataforma, prevenção a fraude) e, quando aplicável, no seu{" "}
          <strong>consentimento</strong> (ex.: cookies não essenciais e comunicações de
          marketing, que podem ser revogados a qualquer momento).
        </p>

        <h2>5. Com quem compartilhamos dados</h2>
        <p>Compartilhamos dados estritamente com fornecedores necessários à operação do
          serviço, sob obrigação contratual de confidencialidade:</p>
        <ul>
          <li><strong>Mercado Pago</strong> — processamento de pagamentos e assinaturas;</li>
          <li><strong>Provedor de hospedagem/infraestrutura em nuvem</strong> — armazenamento
            seguro do banco de dados;</li>
          <li><strong>Google (Tag Manager / Google Ads)</strong> — mensuração de campanhas
            publicitárias do site institucional;</li>
          <li>Autoridades públicas, quando exigido por lei ou ordem judicial.</li>
        </ul>

        <h2>6. Onde e por quanto tempo os dados ficam armazenados</h2>
        <p>
          O {EMPRESA.nomeFantasia} é um sistema 100% em nuvem: os dados do seu negócio ficam
          armazenados em servidores próprios em ambiente de hospedagem em nuvem, protegidos
          por controle de acesso e isolamento entre clientes (cada negócio só acessa os
          próprios dados). Mantemos os dados enquanto durar sua relação contratual com o
          {" "}{EMPRESA.nomeFantasia} e, após o término, pelo prazo necessário para cumprir
          obrigações legais (ex.: fiscais) ou exercício de direitos em processos, sendo
          eliminados ou anonimizados depois disso.
        </p>

        <h2>7. Seus direitos como titular de dados</h2>
        <p>Nos termos do art. 18 da LGPD, você pode, mediante solicitação ao nosso contato,
          exercer os seguintes direitos:</p>
        <ul>
          <li>Confirmação da existência de tratamento e acesso aos dados;</li>
          <li>Correção de dados incompletos, inexatos ou desatualizados;</li>
          <li>Anonimização, bloqueio ou eliminação de dados desnecessários ou tratados em
            desconformidade com a lei;</li>
          <li>Portabilidade dos dados a outro fornecedor de serviço;</li>
          <li>Eliminação dos dados pessoais tratados com consentimento, salvo hipóteses
            legais de retenção;</li>
          <li>Informação sobre com quem seus dados foram compartilhados;</li>
          <li>Revogação do consentimento, quando o tratamento se basear nele.</li>
        </ul>

        <h2>8. Segurança da informação</h2>
        <p>Adotamos medidas técnicas e administrativas para proteger seus dados, incluindo
          senhas armazenadas de forma criptografada (hash), conexão criptografada (HTTPS),
          autenticação por token com expiração e isolamento lógico entre os dados de cada
          negócio (multi-tenant). Nenhum sistema é 100% livre de risco, e trabalhamos
          continuamente para reduzir vulnerabilidades.</p>

        <h2>9. Crianças e adolescentes</h2>
        <p>O {EMPRESA.nomeFantasia} é uma ferramenta de gestão empresarial e não é destinado
          a menores de 18 anos.</p>

        <h2>10. Expansão internacional</h2>
        <p>Estamos avaliando oferecer o {EMPRESA.nomeFantasia} para clientes fora do Brasil.
          Caso isso se concretize, esta política será atualizada para refletir, quando
          aplicável, direitos adicionais previstos em legislações como o GDPR (Regulamento
          Geral de Proteção de Dados da União Europeia), sem prejuízo dos direitos já
          garantidos aqui pela LGPD.</p>

        <h2>11. Alterações desta política</h2>
        <p>Podemos atualizar esta política para refletir mudanças legais ou operacionais. A
          data no topo desta página indica a versão mais recente. Alterações relevantes
          serão comunicadas pelos canais de contato cadastrados.</p>

        <h2>12. Contato</h2>
        <p>Dúvidas ou solicitações sobre seus dados: <a href={`mailto:${EMPRESA.contatoEmail}`}>{EMPRESA.contatoEmail}</a>.</p>
      </>
    ),
    en: (
      <>
        <p>
          This Privacy Policy explains how <strong>{EMPRESA.nomeFantasia}</strong>
          {" "}("we," "the system," or "the platform"), operated by {EMPRESA.razaoSocial}
          {" "}(Brazilian company registration/CNPJ {EMPRESA.cnpj}), collects, uses, stores,
          and protects the personal data of visitors to our website and users of our
          management panel, in accordance with Brazil's General Data Protection Law (Law
          No. 13,709/2018 — LGPD).
        </p>

        <h2>1. Who is the data controller</h2>
        <p>
          The data controller for information processed on this platform is {EMPRESA.razaoSocial},
          CNPJ {EMPRESA.cnpj}, headquartered in {EMPRESA.cidade}, {EMPRESA.uf}, Brazil. For
          any privacy or data protection matter, data subjects may contact us at{" "}
          <a href={`mailto:${EMPRESA.contatoEmail}`}>{EMPRESA.contatoEmail}</a>.
        </p>

        <h2>2. What data we collect</h2>
        <p><strong>a) Data you provide</strong> when filling out the site's contact form,
          creating an account, or using the panel: name, email, WhatsApp/phone number,
          business name and, where applicable, tax ID (CPF/CNPJ).</p>
        <p><strong>b) Operational data</strong> entered by you (or your team) during normal
          use of the system to manage your own business: customer records, vehicles,
          service orders, quotes, inventory items, and financial entries. This data belongs
          to you and your business — we use it only to operate the platform on your behalf.</p>
        <p><strong>c) Browsing and usage data</strong>, collected automatically: IP address,
          device/browser type, pages visited, and cookies (see our{" "}
          <a href="/cookies">Cookie Policy</a>).</p>
        <p><strong>d) Payment data</strong>: when you subscribe to a paid plan, card/Pix
          processing is handled directly by Mercado Pago; we store only the billing status
          and history, never the full card number.</p>

        <h2>3. How we use your data</h2>
        <ul>
          <li>Enable your access to and the operation of the management panel you subscribed to;</li>
          <li>Process charges and maintain your billing and subscription history;</li>
          <li>Provide technical and commercial support;</li>
          <li>Send operational communications (signup confirmation, password recovery,
            billing/license notices);</li>
          <li>Comply with legal and tax obligations;</li>
          <li>Measure the performance of our marketing campaigns (see the Cookies section);</li>
          <li>Improve the platform based on aggregated, anonymous usage.</li>
        </ul>
        <p>We do not sell personal data to third parties.</p>

        <h2>4. Legal basis (LGPD, art. 7)</h2>
        <p>
          We process your data based on <strong>contract performance</strong> (to provide
          the service you contracted), <strong>compliance with legal or regulatory
          obligations</strong> (e.g., tax records), <strong>legitimate interest</strong>{" "}
          (platform security, fraud prevention) and, where applicable, your{" "}
          <strong>consent</strong> (e.g., non-essential cookies and marketing
          communications, which can be withdrawn at any time).
        </p>

        <h2>5. Who we share data with</h2>
        <p>We share data strictly with vendors necessary to operate the service, under
          contractual confidentiality obligations:</p>
        <ul>
          <li><strong>Mercado Pago</strong> — payment and subscription processing;</li>
          <li><strong>Cloud hosting/infrastructure provider</strong> — secure database storage;</li>
          <li><strong>Google (Tag Manager / Google Ads)</strong> — measurement of the
            institutional site's advertising campaigns;</li>
          <li>Public authorities, when required by law or court order.</li>
        </ul>

        <h2>6. Where and how long data is stored</h2>
        <p>
          {EMPRESA.nomeFantasia} is a 100% cloud-based system: your business data is stored
          on our own servers in a cloud hosting environment, protected by access control
          and isolation between customers (each business only accesses its own data). We
          retain data for as long as your contractual relationship with {EMPRESA.nomeFantasia}{" "}
          lasts and, after termination, for the period required to comply with legal
          obligations (e.g., tax) or to exercise rights in legal proceedings, after which
          it is deleted or anonymized.
        </p>

        <h2>7. Your rights as a data subject</h2>
        <p>Under art. 18 of the LGPD, you may request from us, through our contact channel:</p>
        <ul>
          <li>Confirmation that processing exists and access to your data;</li>
          <li>Correction of incomplete, inaccurate, or outdated data;</li>
          <li>Anonymization, blocking, or deletion of unnecessary data or data processed
            in violation of the law;</li>
          <li>Data portability to another service provider;</li>
          <li>Deletion of personal data processed with consent, except where the law
            requires retention;</li>
          <li>Information about who your data has been shared with;</li>
          <li>Withdrawal of consent, when processing is based on it.</li>
        </ul>

        <h2>8. Information security</h2>
        <p>We adopt technical and administrative measures to protect your data, including
          encrypted (hashed) password storage, encrypted connections (HTTPS), token-based
          authentication with expiration, and logical isolation between each business's
          data (multi-tenant). No system is 100% risk-free, and we work continuously to
          reduce vulnerabilities.</p>

        <h2>9. Children and minors</h2>
        <p>{EMPRESA.nomeFantasia} is a business management tool and is not intended for use
          by anyone under 18.</p>

        <h2>10. International expansion</h2>
        <p>We are evaluating offering {EMPRESA.nomeFantasia} to customers outside Brazil.
          Should this happen, this policy will be updated to reflect, where applicable,
          additional rights provided under laws such as the GDPR (EU General Data
          Protection Regulation), without prejudice to the rights already guaranteed here
          under the LGPD.</p>

        <h2>11. Changes to this policy</h2>
        <p>We may update this policy to reflect legal or operational changes. The date at
          the top of this page indicates the most recent version. Material changes will
          be communicated through your registered contact channels.</p>

        <h2>12. Contact</h2>
        <p>Questions or requests about your data: <a href={`mailto:${EMPRESA.contatoEmail}`}>{EMPRESA.contatoEmail}</a>.</p>
      </>
    ),
    es: (
      <>
        <p>
          Esta Política de Privacidad explica cómo <strong>{EMPRESA.nomeFantasia}</strong>
          {" "}("nosotros", "el sistema" o "la plataforma"), operado por {EMPRESA.razaoSocial}
          {" "}(CNPJ {EMPRESA.cnpj}, registro de persona jurídica en Brasil), recopila, usa,
          almacena y protege los datos personales de quienes visitan nuestro sitio web o
          utilizan el panel de gestión, de conformidad con la Ley General de Protección de
          Datos de Brasil (Ley n.º 13.709/2018 — LGPD).
        </p>

        <h2>1. Quién es el responsable del tratamiento</h2>
        <p>
          El responsable del tratamiento de los datos en esta plataforma es {EMPRESA.razaoSocial},
          {" "}CNPJ {EMPRESA.cnpj}, con sede en {EMPRESA.cidade}, {EMPRESA.uf}, Brasil. Para
          cualquier consulta sobre privacidad y protección de datos, puede escribirnos a{" "}
          <a href={`mailto:${EMPRESA.contatoEmail}`}>{EMPRESA.contatoEmail}</a>.
        </p>

        <h2>2. Qué datos recopilamos</h2>
        <p><strong>a) Datos que usted proporciona</strong> al completar el formulario de
          contacto del sitio, crear una cuenta o usar el panel: nombre, correo electrónico,
          WhatsApp/teléfono, nombre del negocio y, cuando corresponda, CPF/CNPJ.</p>
        <p><strong>b) Datos operativos</strong> que usted (o su equipo) ingresa durante el
          uso normal del sistema para gestionar su propio negocio: registro de clientes,
          vehículos, órdenes de servicio, presupuestos, artículos de inventario y
          movimientos financieros. Estos datos le pertenecen a usted y a su negocio — los
          usamos únicamente para operar la plataforma en su nombre.</p>
        <p><strong>c) Datos de navegación y uso</strong>, recopilados automáticamente:
          dirección IP, tipo de dispositivo/navegador, páginas visitadas y cookies (vea
          nuestra <a href="/cookies">Política de Cookies</a>).</p>
        <p><strong>d) Datos de pago</strong>: cuando usted contrata un plan pago, el
          procesamiento de la tarjeta/Pix lo realiza directamente Mercado Pago; nosotros
          almacenamos solo el estado y el historial del cobro, nunca el número completo de
          la tarjeta.</p>

        <h2>3. Para qué usamos sus datos</h2>
        <ul>
          <li>Habilitar su acceso y el funcionamiento del panel de gestión contratado;</li>
          <li>Procesar cobros y mantener el historial de pagos y suscripciones;</li>
          <li>Brindar soporte técnico y comercial;</li>
          <li>Enviar comunicaciones operativas (confirmación de registro, recuperación de
            contraseña, avisos de cobro/licencia);</li>
          <li>Cumplir con obligaciones legales y fiscales;</li>
          <li>Medir el rendimiento de nuestras campañas de difusión (vea la sección de
            Cookies);</li>
          <li>Mejorar la plataforma con base en el uso agregado y anónimo.</li>
        </ul>
        <p>No vendemos datos personales a terceros.</p>

        <h2>4. Base legal (LGPD, art. 7)</h2>
        <p>
          Tratamos sus datos con base en la <strong>ejecución del contrato</strong> (para
          prestar el servicio contratado), el <strong>cumplimiento de obligaciones legales
          o regulatorias</strong> (p. ej., registros fiscales), el <strong>interés
          legítimo</strong> (seguridad de la plataforma, prevención de fraude) y, cuando
          corresponda, su <strong>consentimiento</strong> (p. ej., cookies no esenciales y
          comunicaciones de marketing, revocable en cualquier momento).
        </p>

        <h2>5. Con quién compartimos los datos</h2>
        <p>Compartimos datos estrictamente con proveedores necesarios para la operación
          del servicio, bajo obligación contractual de confidencialidad:</p>
        <ul>
          <li><strong>Mercado Pago</strong> — procesamiento de pagos y suscripciones;</li>
          <li><strong>Proveedor de hospedaje/infraestructura en la nube</strong> —
            almacenamiento seguro de la base de datos;</li>
          <li><strong>Google (Tag Manager / Google Ads)</strong> — medición de campañas
            publicitarias del sitio institucional;</li>
          <li>Autoridades públicas, cuando lo exija la ley o una orden judicial.</li>
        </ul>

        <h2>6. Dónde y por cuánto tiempo se almacenan los datos</h2>
        <p>
          {EMPRESA.nomeFantasia} es un sistema 100% en la nube: los datos de su negocio se
          almacenan en servidores propios en un entorno de hospedaje en la nube, protegidos
          por control de acceso y aislamiento entre clientes (cada negocio solo accede a
          sus propios datos). Conservamos los datos mientras dure su relación contractual
          con {EMPRESA.nomeFantasia} y, tras su finalización, por el plazo necesario para
          cumplir obligaciones legales (p. ej., fiscales) o ejercer derechos en procesos,
          siendo eliminados o anonimizados después.
        </p>

        <h2>7. Sus derechos como titular de los datos</h2>
        <p>Conforme al art. 18 de la LGPD, puede solicitarnos, a través de nuestro
          contacto:</p>
        <ul>
          <li>Confirmación de la existencia de tratamiento y acceso a los datos;</li>
          <li>Corrección de datos incompletos, inexactos o desactualizados;</li>
          <li>Anonimización, bloqueo o eliminación de datos innecesarios o tratados en
            desconformidad con la ley;</li>
          <li>Portabilidad de los datos a otro proveedor de servicios;</li>
          <li>Eliminación de los datos personales tratados con consentimiento, salvo
            hipótesis legales de retención;</li>
          <li>Información sobre con quién se compartieron sus datos;</li>
          <li>Revocación del consentimiento, cuando el tratamiento se base en él.</li>
        </ul>

        <h2>8. Seguridad de la información</h2>
        <p>Adoptamos medidas técnicas y administrativas para proteger sus datos, incluyendo
          contraseñas almacenadas de forma cifrada (hash), conexión cifrada (HTTPS),
          autenticación por token con expiración y aislamiento lógico entre los datos de
          cada negocio (multi-tenant). Ningún sistema está 100% libre de riesgos, y
          trabajamos continuamente para reducir vulnerabilidades.</p>

        <h2>9. Niños y adolescentes</h2>
        <p>{EMPRESA.nomeFantasia} es una herramienta de gestión empresarial y no está
          destinada a menores de 18 años.</p>

        <h2>10. Expansión internacional</h2>
        <p>Estamos evaluando ofrecer {EMPRESA.nomeFantasia} a clientes fuera de Brasil. Si
          esto se concreta, esta política será actualizada para reflejar, cuando
          corresponda, derechos adicionales previstos en legislaciones como el RGPD
          (Reglamento General de Protección de Datos de la Unión Europea), sin perjuicio de
          los derechos ya garantizados aquí por la LGPD.</p>

        <h2>11. Cambios en esta política</h2>
        <p>Podemos actualizar esta política para reflejar cambios legales u operativos. La
          fecha en la parte superior de esta página indica la versión más reciente. Los
          cambios relevantes serán comunicados por los canales de contacto registrados.</p>

        <h2>12. Contacto</h2>
        <p>Dudas o solicitudes sobre sus datos: <a href={`mailto:${EMPRESA.contatoEmail}`}>{EMPRESA.contatoEmail}</a>.</p>
      </>
    ),
  };

  return (
    <LegalLayout title={TITULOS[lang] || TITULOS.pt} updatedAt={ATUALIZADO[lang] || ATUALIZADO.pt}>
      {CONTEUDO[lang] || CONTEUDO.pt}
    </LegalLayout>
  );
}
